import { Deferred, Effect } from 'effect'
import { FileReadError } from './archive-errors'
import { browserFileEntry } from './browser-file'
import type { FileEntry } from './file-entry'
import type { UnpackOpts, UploadDirectory, UploadSelection } from './unpack-upload'

/**
 * Resolves event-scoped DataTransfer objects into durable Effect-native file
 * capabilities. No downstream code calls browser file-system callbacks.
 */
export function captureUpload(upload: File[] | FileList | DataTransfer): UploadSelection {
  if (!isDataTransfer(upload)) {
    return { items: Array.from(upload, captureFile) }
  }

  const items: UploadSelection['items'][number][] = []
  for (const item of Array.from(upload.items)) {
    if (item.kind != 'file') continue

    const entry = item.webkitGetAsEntry?.()
    if (entry && isDirectoryEntry(entry)) {
      items.push({ type: 'directory', directory: captureDirectory(entry) })
      continue
    }

    const file = item.getAsFile()
    if (file) items.push(captureFile(file))
  }

  return { items }
}

function captureFile(file: File): UploadSelection['items'][number] {
  return { type: 'file', entry: browserFileEntry(file.name, file) }
}

function captureDirectory(entry: FileSystemDirectoryEntry): UploadDirectory {
  return {
    name: entry.name,
    readEntries: (shouldIgnore) => readFileSystemEntry(entry, '', shouldIgnore)
  }
}

function readFileSystemEntry(
  entry: FileSystemEntry,
  dirname: string,
  shouldIgnore: UnpackOpts['shouldIgnore']
): Effect.Effect<FileEntry[], FileReadError> {
  if (isFileEntry(entry)) {
    return readFile(entry).pipe(
      Effect.map((file) => [browserFileEntry(joinPath(dirname, file.name), file)])
    )
  }

  if (isDirectoryEntry(entry)) {
    return readDirectory(entry, shouldIgnore).pipe(
      Effect.flatMap((entries) =>
        Effect.forEach(
          entries,
          (child) => readFileSystemEntry(child, joinPath(dirname, entry.name), shouldIgnore),
          { concurrency: 'unbounded' }
        )
      ),
      Effect.map((entries) => entries.flat())
    )
  }

  return Effect.die(new Error('Unsupported browser file-system entry type'))
}

function readFile(entry: FileSystemFileEntry): Effect.Effect<File, FileReadError> {
  return Effect.gen(function* () {
    const completed = yield* Deferred.make<File, FileReadError>()
    yield* Effect.try({
      try: () =>
        entry.file(
          (file) => Deferred.doneUnsafe(completed, Effect.succeed(file)),
          (cause) => Deferred.doneUnsafe(completed, Effect.fail(new FileReadError({ cause })))
        ),
      catch: (cause) => new FileReadError({ cause })
    })
    return yield* Deferred.await(completed)
  })
}

function readDirectory(
  entry: FileSystemDirectoryEntry,
  shouldIgnore: UnpackOpts['shouldIgnore']
): Effect.Effect<FileSystemEntry[], FileReadError> {
  return Effect.gen(function* () {
    const reader = yield* Effect.try({
      try: () => entry.createReader(),
      catch: (cause) => new FileReadError({ cause })
    })
    const entries: FileSystemEntry[] = []

    while (true) {
      const batch = yield* readDirectoryBatch(reader)
      if (batch.length == 0) return entries
      entries.push(...batch.filter((child) => !shouldIgnore(child.name)))
    }
  })
}

function readDirectoryBatch(
  reader: FileSystemDirectoryReader
): Effect.Effect<FileSystemEntry[], FileReadError> {
  return Effect.gen(function* () {
    const completed = yield* Deferred.make<FileSystemEntry[], FileReadError>()
    yield* Effect.try({
      try: () =>
        reader.readEntries(
          (entries) => Deferred.doneUnsafe(completed, Effect.succeed(entries ?? [])),
          (cause) => Deferred.doneUnsafe(completed, Effect.fail(new FileReadError({ cause })))
        ),
      catch: (cause) => new FileReadError({ cause })
    })
    return yield* Deferred.await(completed)
  })
}

function isDataTransfer(upload: File[] | FileList | DataTransfer): upload is DataTransfer {
  return 'items' in upload
}

function isFileEntry(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile
}

function isDirectoryEntry(entry: FileSystemEntry): entry is FileSystemDirectoryEntry {
  return entry.isDirectory
}

function joinPath(a: string, b: string) {
  if (a == '') return b
  return `${a}/${b}`
}
